import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import moment from 'moment';
import { useMemo } from 'react';
import { RxUpdate } from 'react-icons/rx';
import { TbSquareRoundedPlusFilled } from 'react-icons/tb';
import { toast } from 'react-toastify';
import { SELECT_FIELD_DEFAULT_VALUE } from '../../../constants/settings';
import { useAuthContext } from '../../../contexts/AuthProvider';
import useConfigActions from '../../../hooks/APIHooks/appointments/useConfigActions';
import useRetainAgreementActions from '../../../hooks/APIHooks/appointments/useRetainAgreementActions';
import useClientActions from '../../../hooks/APIHooks/users/useClientActions';
import { createEditRetainAgreementSchema } from '../../../schemas';
import { RetainAgreement } from '../../../types/retain';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaDatePicker from '../Inputs/AcademiaDatePicker';
import AcademiaFileInputField from '../Inputs/AcademiaFileInputField';
import AcademiaFormSelect from '../Inputs/AcademiaFormSelect';
import AcademiaInputField from '../Inputs/AcademiaInputField';
import AcademiaTextAreaField from '../Inputs/AcademiaTextAreaField';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const CreateEditRetainAgreementModal = ({
  title,
  retainAgreement,
  handleModalClose,
}: {
  title: 'create' | 'update';
  retainAgreement?: RetainAgreement;
  handleModalClose: () => void;
}) => {
  // integration of context hooks here
  const { currentUser } = useAuthContext();

  // integration of custom API hooks here
  const { clientListHandler } = useClientActions('clientListHandler');
  const { getServiceConfigsByIdHandler } = useConfigActions(
    '',
    currentUser?.id
  );

  const { createNewRetainAgreementHandler, updateRetainAgreementByIdHandler } =
    useRetainAgreementActions('');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  const clientOptions = useMemo(() => {
    if (!clientListHandler.data?.data) return [];

    return clientListHandler.data.data.map((client) => ({
      id: client.id,
      name: client.client_full_name,
    }));
  }, [clientListHandler.data]);

  const serviceOptions = useMemo(() => {
    if (!getServiceConfigsByIdHandler.data?.data) return [];

    return getServiceConfigsByIdHandler.data.data.map((serviceConfig) => ({
      id: serviceConfig.id,
      name: serviceConfig.service_data.name,
    }));
  }, [getServiceConfigsByIdHandler.data]);

  const createEditRetainAgreementHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      console.log(values);

      const body = new FormData();

      // mandatory fields
      body.append('associate', (currentUser?.id as number).toString());
      body.append('client', values.client.id);
      body.append('service_config', values.service_config.id);
      body.append(
        'start_date',
        moment(values.start_date, 'DD-MM-YYYY').format('YYYY-MM-DD')
      );
      body.append(
        'end_date',
        moment(values.end_date, 'DD-MM-YYYY').format('YYYY-MM-DD')
      );
      body.append('monthly_fee', values.monthly_fee);
      body.append('additional_hour_rate', values.additional_hour_rate);
      body.append('hours_included', values.hours_included);

      // optional fields
      values.document_notes &&
        body.append('document_notes', values.document_notes);

      if (!retainAgreement) {
        body.append('document', values.document);
        body.append('created_by', currentUser?.username as string);

        await createNewRetainAgreementHandler
          .mutateAsync({ body })
          .then((res) => {
            if (res.is_success) {
              toast.success('Retain Agreement Created!!');

              handleModalClose();
              queryClient.invalidateQueries({
                queryKey: ['retain-agreement-list'],
              });
            } else {
              toast.error(res.message || 'Agreement Creation Failed!!');
            }
          })
          .catch((error) => toast.error(fetchErrorMessage(error)));
      } else {
        values.document && body.append('document', values.document);
        body.append('updated_by', currentUser?.username as string);

        await updateRetainAgreementByIdHandler
          .mutateAsync({ agreementId: retainAgreement.id, body })
          .then((res) => {
            if (res.is_success) {
              toast.success('Retain Agreement Updated!!');

              handleModalClose();
              queryClient.invalidateQueries({
                queryKey: ['retain-agreement-list'],
              });
            } else {
              toast.error(res.message || 'Agreement Updating Failed!!');
            }
          });
      }
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering create/edit retain agreement modal component here
  return (
    <ParentModal onClick={handleModalClose} customWidthClass='md:w-[600px]'>
      <ModalTitle titleText={`${title} agreement`} />

      <Formik
        initialValues={{
          is_editing: retainAgreement ? true : false,
          client: retainAgreement
            ? {
                id: retainAgreement.client,
                name: retainAgreement.client_full_name,
              }
            : SELECT_FIELD_DEFAULT_VALUE,
          service_config: retainAgreement
            ? {
                id: retainAgreement.service_config,
                name: retainAgreement.service_config_name,
              }
            : SELECT_FIELD_DEFAULT_VALUE,
          start_date: retainAgreement?.start_date || '',
          end_date: retainAgreement?.end_date || '',
          monthly_fee: retainAgreement?.monthly_fee || '',
          additional_hour_rate: retainAgreement?.additional_hour_rate || '',
          hours_included: retainAgreement?.hours_included || '',
          document: '',
          document_notes: retainAgreement?.document_notes || '',
        }}
        validationSchema={createEditRetainAgreementSchema}
        onSubmit={createEditRetainAgreementHandler}
      >
        {(props) => (
          <Form className='space-y-4'>
            <AcademiaFormSelect
              name='client'
              label='Select Client'
              options={clientOptions}
              defaultValue={props.values.client}
              showSearchInput
              isRequired
            />

            <AcademiaFormSelect
              name='service_config'
              label='Select Service'
              options={serviceOptions}
              defaultValue={props.values.service_config}
              showSearchInput
              isRequired
            />

            <div className='grid md:grid-cols-2 gap-4'>
              <AcademiaDatePicker
                name='start_date'
                label='Agreement Starting Date'
                placeholder='DD-MM-YYYY'
                maxDate={new Date()}
                defaultDate={
                  retainAgreement
                    ? new Date(retainAgreement.start_date)
                    : new Date()
                }
                isRequired
              />

              <AcademiaDatePicker
                name='end_date'
                label='Agreement Ending Date'
                placeholder='DD-MM-YYYY'
                minDate={new Date()}
                defaultDate={
                  retainAgreement
                    ? new Date(retainAgreement.end_date)
                    : new Date()
                }
                isRequired
              />
            </div>

            <AcademiaInputField
              type='number'
              name='monthly_fee'
              label='Monthly Fee'
              placeholder='Enter monthly fee here...'
              isRequired
            />

            <AcademiaInputField
              type='number'
              name='additional_hour_rate'
              label='Additional Hourly Rate'
              placeholder='Enter additional hourly rate here...'
              isRequired
            />

            <AcademiaInputField
              type='number'
              name='hours_included'
              label='Total Hours Per Week'
              placeholder='Enter total hours per week here...'
              isRequired
            />

            <AcademiaFileInputField
              name='document'
              label='Agreement Document'
              btnText='Upload'
              accept='.docx, .pdf'
              placeholder='Upload agreement document here...'
              filePath={retainAgreement ? retainAgreement.document : undefined}
              isRequired
            />

            <AcademiaTextAreaField
              name='document_notes'
              label='Document Notes'
              placeholder='Type additional document notes here...'
            />

            <AcademiaButton
              title={
                props.values.client.id === -1
                  ? 'Please, select a client first!'
                  : props.values.service_config.id === -1
                  ? 'Please, select a service_config first!'
                  : undefined
              }
              type='submit'
              variant='premium'
              icon={
                title === 'create' ? (
                  <TbSquareRoundedPlusFilled className='size-6' />
                ) : (
                  <RxUpdate className='size-6' />
                )
              }
              isLoading={props.isSubmitting}
              additionalClassNames='!mt-7'
              fullWidth
              isDisabled={
                props.values.client.id === -1 ||
                props.values.service_config.id === -1
              }
            >
              {title} Agreement
            </AcademiaButton>
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default CreateEditRetainAgreementModal;
